/**
 * Who else is playing: the game's side of the realtime room.
 *
 * One WebSocket to `/rt/ws` (the Worker in `realtime/`), reconnecting with
 * backoff when it drops — and staying quiet when there is no room at all, as
 * in local development without `wrangler dev`: multiplayer is a bonus, never
 * a reason for the game to fail.
 *
 * The player's position goes out about once a second, and only when it has
 * changed — four times a second while anyone else is within `NEAR`, so they
 * see this player move smoothly. That rate is the cost (see
 * docs/multiplayer.md). The room sends everyone back four times a second.
 *
 * Privacy: a player can hide, with the 👥 chip on the world map. Otherwise
 * they are shown wherever they are, their own location included.
 */

import type { PlayerMode } from '../config.js';
import type { FallStart } from '../core/character/fall.js';
import type { ClientMessage, PlayerRow } from './protocol.js';
import { PROTOCOL_VERSION, fallFromWire, fallToWire, parseRoomMessage, parseWireFall, randomName } from './protocol.js';
import { ClockSync, Track } from './tracks.js';

export interface RemotePlayer {
  id: string;
  name: string;
  lat: number;
  lon: number;
  alt: number;
  heading: number;
  mode: PlayerMode;
  /** Down: how they fell. */
  fall?: FallStart;
}

export interface OwnState {
  lat: number;
  lon: number;
  altitude: number;
  heading: number;
  mode: PlayerMode;
  /** Down, and how they fell: sent with every position until they are up. */
  fall?: FallStart;
}

const SEND_MS = 1000;
/** Sent this often while another player is near enough to be seen moving. */
const SEND_NEAR_MS = 250;
/** Near enough, metres. */
export const NEAR = 2000;
/** Sent at least this often while standing still, so the room knows we are here. */
const KEEPALIVE_MS = 15_000;
const PING_MS = 25_000;

const KEY_NAME = 'wsg.mp.name';
const KEY_VISIBLE = 'wsg.mp.visible';
/** Where earlier versions kept spots to hide near; cleared on start. */
const KEY_PRIVATE = 'wsg.mp.private';

function load(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function save(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* private mode */ }
}

function metres(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const k = Math.PI / 180;
  const x = (bLon - aLon) * k * Math.cos(((aLat + bLat) / 2) * k), y = (bLat - aLat) * k;
  return Math.hypot(x, y) * 6_371_000;
}

export class Presence {
  /** Everyone else in the room who is showing, by id: their latest position. */
  readonly players = new Map<string, RemotePlayer>();
  /** Their movement, for drawing them smoothly. See `tracks.ts`. */
  readonly tracks = new Map<string, Track>();
  private readonly clock = new ClockSync();
  /** Everyone connected, shown or not, ourselves included; 0 when not connected. */
  online = 0;
  connected = false;
  readonly name: string;
  private visibleFlag: boolean;
  private id: string | undefined;
  private socket: WebSocket | undefined;
  private retry = 1000;
  private lastSent = 0;
  private last: { lat: number; lon: number; heading: number; mode: PlayerMode; down: boolean } | undefined;
  private hiddenSent = false;
  private moving = false;
  private pingTimer: number | undefined;
  /** Called whenever the list or the count changes. */
  onChange: () => void = () => {};

  constructor(private readonly url: string) {
    this.name = load(KEY_NAME) ?? randomName();
    save(KEY_NAME, this.name);
    this.visibleFlag = load(KEY_VISIBLE) !== '0';
    try { localStorage.removeItem(KEY_PRIVATE); } catch { /* private mode */ }
  }

  get visible(): boolean {
    return this.visibleFlag;
  }

  set visible(on: boolean) {
    this.visibleFlag = on;
    save(KEY_VISIBLE, on ? '1' : '0');
    this.lastSent = 0;
    this.onChange();
  }

  connect(): void {
    if (this.socket !== undefined) return;
    let ws: WebSocket;
    try { ws = new WebSocket(this.url); } catch { this.scheduleRetry(); return; }
    this.socket = ws;
    ws.addEventListener('open', () => {
      this.connected = true;
      this.retry = 1000;
      this.lastSent = 0;
      this.hiddenSent = false;
      this.send({ t: 'hi', v: PROTOCOL_VERSION, name: this.name });
      this.pingTimer = window.setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, PING_MS);
    });
    ws.addEventListener('message', (event) => {
      if (event.data === 'pong') return;
      const m = parseRoomMessage(event.data);
      if (m === undefined) return;
      if (m.t === 'welcome') { this.id = m.id; return; }
      this.online = m.online;
      const now = performance.now();
      if (typeof m.now === 'number') this.clock.note(m.now, now);
      this.players.clear();
      for (const r of m.players) {
        if (r[0] === this.id) continue;
        const p = fromRow(r);
        this.players.set(p.id, p);
        let track = this.tracks.get(p.id);
        if (track === undefined) this.tracks.set(p.id, (track = new Track()));
        track.add({ t: typeof r[7] === 'number' ? this.clock.toLocal(r[7]) : now, lat: p.lat, lon: p.lon, alt: p.alt, heading: p.heading, mode: p.mode, fall: p.fall });
      }
      for (const id of this.tracks.keys()) if (!this.players.has(id)) this.tracks.delete(id);
      this.onChange();
    });
    ws.addEventListener('close', (event) => {
      window.clearInterval(this.pingTimer);
      this.socket = undefined;
      this.connected = false;
      this.online = 0;
      this.players.clear();
      this.tracks.clear();
      this.onChange();
      // 4000: the room speaks a newer protocol; reconnecting will not help.
      if (event.code !== 4000) this.scheduleRetry();
    });
  }

  /**
   * Say where we are now, whatever is due: for the moment before the browser
   * pauses a hidden tab, so its last word is not the top of a jump.
   */
  sendNow(state: OwnState): void {
    if (!this.connected || this.hiddenSent) return;
    this.lastSent = performance.now();
    this.moving = false;
    this.last = { lat: state.lat, lon: state.lon, heading: state.heading, mode: state.mode, down: state.fall !== undefined };
    this.send(at(state));
  }

  /** Once a frame: tell the room where we are, when that is due. */
  update(state: OwnState, now: number): void {
    if (!this.connected) return;
    if (!this.visibleFlag) {
      if (!this.hiddenSent) { this.send({ t: 'hide' }); this.hiddenSent = true; this.last = undefined; }
      return;
    }
    this.hiddenSent = false;
    const near = [...this.players.values()].some(p => metres(p.lat, p.lon, state.lat, state.lon) < NEAR);
    if (now - this.lastSent < (near ? SEND_NEAR_MS : SEND_MS)) return;
    const l = this.last;
    // Near others, every step counts; on a map, only a couple of metres do.
    const moved = l === undefined || l.mode !== state.mode || l.down !== (state.fall !== undefined)
      || metres(l.lat, l.lon, state.lat, state.lon) > (near ? 0.15 : 2)
      || Math.abs((((state.heading - l.heading) % 360) + 540) % 360 - 180) > (near ? 2 : 8);
    // One more once it stops, so others see it stand still rather than coast on.
    const stopping = !moved && this.moving;
    if (!moved && !stopping && now - this.lastSent < KEEPALIVE_MS) return;
    this.moving = moved;
    this.lastSent = now;
    this.last = { lat: state.lat, lon: state.lon, heading: state.heading, mode: state.mode, down: state.fall !== undefined };
    this.send(at(state));
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  private scheduleRetry(): void {
    const wait = this.retry * (0.75 + Math.random() * 0.5);
    this.retry = Math.min(60_000, this.retry * 2);
    window.setTimeout(() => this.connect(), wait);
  }
}

function fromRow(r: PlayerRow): RemotePlayer {
  const fall = parseWireFall(r[8]);
  const p: RemotePlayer = { id: r[0], name: r[1], lat: r[2], lon: r[3], alt: r[4], heading: r[5], mode: r[6] };
  if (fall !== undefined) p.fall = fallFromWire(fall);
  return p;
}

/** Where we are, for the room; how we fell, while down. */
function at(state: OwnState): ClientMessage {
  const m: ClientMessage = { t: 'at', lat: state.lat, lon: state.lon, alt: state.altitude, hdg: state.heading, mode: state.mode };
  if (state.fall !== undefined) m.fall = fallToWire(state.fall);
  return m;
}

/** Where the room is: the page's own host, under `/rt/` (the dev server proxies it). */
export function realtimeUrl(location: Location): string {
  const override = (import.meta.env.VITE_REALTIME_URL as string | undefined) ?? '';
  if (override !== '') return override;
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/rt/ws`;
}
